import { z } from 'zod';
import { AssignmentsSchema } from './assignments.js';
import { IdSchema, ListQuerySchema, MoneyAmountSchema, PageInfoSchema, TimestampSchema, VersionSchema, } from './common.js';
export const WorkflowPhaseSchema = z.enum(['intake', 'delivery']);
export const MilestoneStateSchema = z.enum([
    'not_scheduled',
    'scheduled',
    'in_progress',
    'submitted',
    'approved',
    'rejected',
    'completed',
    'skipped',
    'move_requested',
]);
export const MilestoneTaskInputSchema = z.enum([
    'checkbox',
    'text',
    'number',
    'file',
    'photo',
    'signature',
]);
const KeySchema = z
    .string()
    .trim()
    .regex(/^[a-z0-9]+(?:_[a-z0-9]+)*$/, 'Use lowercase letters, digits, and single underscores')
    .max(80);
// ---------- templates ----------
export const MilestoneTemplateTaskSchema = z.strictObject({
    id: IdSchema,
    templateId: IdSchema,
    key: z.string(),
    label: z.string(),
    description: z.string().nullable(),
    input: MilestoneTaskInputSchema,
    required: z.boolean(),
    evidenceAllowed: z.boolean(),
    sortOrder: z.number().int(),
    active: z.boolean(),
});
export const MilestoneTemplateSchema = z.strictObject({
    id: IdSchema,
    organizationId: IdSchema,
    key: z.string(),
    name: z.string(),
    description: z.string().nullable(),
    phase: WorkflowPhaseSchema,
    workCategoryId: IdSchema.nullable(),
    requiresApproval: z.boolean(),
    active: z.boolean(),
    sortOrder: z.number().int(),
    packVersion: z.string().nullable(),
    tasks: z.array(MilestoneTemplateTaskSchema),
    version: VersionSchema,
    createdAt: TimestampSchema,
    updatedAt: TimestampSchema,
});
export const CreateMilestoneTemplateTaskRequestSchema = z.strictObject({
    key: KeySchema,
    label: z.string().trim().min(1).max(200),
    description: z.string().trim().max(1000).nullable().default(null),
    input: MilestoneTaskInputSchema.default('checkbox'),
    required: z.boolean().default(true),
    evidenceAllowed: z.boolean().default(false),
    sortOrder: z.number().int().min(0).max(10_000).optional(),
});
export const UpdateMilestoneTemplateTaskRequestSchema = z.strictObject({
    label: z.string().trim().min(1).max(200).optional(),
    description: z.string().trim().max(1000).nullable().optional(),
    input: MilestoneTaskInputSchema.optional(),
    required: z.boolean().optional(),
    evidenceAllowed: z.boolean().optional(),
    sortOrder: z.number().int().min(0).max(10_000).optional(),
    active: z.boolean().optional(),
    version: VersionSchema,
});
export const CreateMilestoneTemplateRequestSchema = z.strictObject({
    key: KeySchema,
    name: z.string().trim().min(1).max(200),
    description: z.string().trim().max(2000).nullable().default(null),
    phase: WorkflowPhaseSchema,
    workCategoryId: IdSchema.nullable().default(null),
    requiresApproval: z.boolean().default(false),
    sortOrder: z.number().int().min(0).max(10_000).optional(),
    tasks: z.array(CreateMilestoneTemplateTaskRequestSchema).max(50).default([]),
});
/** Phase is immutable once any workflow run references the template. */
export const UpdateMilestoneTemplateRequestSchema = z.strictObject({
    name: z.string().trim().min(1).max(200).optional(),
    description: z.string().trim().max(2000).nullable().optional(),
    phase: WorkflowPhaseSchema.optional(),
    workCategoryId: IdSchema.nullable().optional(),
    requiresApproval: z.boolean().optional(),
    sortOrder: z.number().int().min(0).max(10_000).optional(),
    active: z.boolean().optional(),
    version: VersionSchema,
});
export const MilestoneTemplateListQuerySchema = ListQuerySchema.extend({
    phase: WorkflowPhaseSchema.optional(),
    workCategoryId: IdSchema.optional(),
    includeInactive: z.stringbool().default(false),
});
export const MilestoneTemplateListSchema = z.strictObject({
    items: z.array(MilestoneTemplateSchema),
    page: PageInfoSchema,
});
export const InstallLaunchPackResultSchema = z.strictObject({
    packVersion: z.string(),
    installed: z.number().int().nonnegative(),
    skipped: z.number().int().nonnegative(),
});
// ---------- runs and milestones ----------
export const WorkflowRunSchema = z.strictObject({
    id: IdSchema,
    organizationId: IdSchema,
    jobFileId: IdSchema,
    projectId: IdSchema.nullable(),
    phase: WorkflowPhaseSchema,
    name: z.string(),
    startedAt: TimestampSchema,
    completedAt: TimestampSchema.nullable(),
    milestoneCount: z.number().int().nonnegative(),
    completedMilestoneCount: z.number().int().nonnegative(),
    version: VersionSchema,
    createdAt: TimestampSchema,
    updatedAt: TimestampSchema,
});
/** Instantiates every listed template (or all active templates of the phase) into one run. */
export const CreateWorkflowRunRequestSchema = z.strictObject({
    jobFileId: IdSchema,
    phase: WorkflowPhaseSchema,
    projectId: IdSchema.nullable().default(null),
    name: z.string().trim().min(1).max(200).optional(),
    templateIds: z.array(IdSchema).max(50).optional(),
});
export const AddMilestoneRequestSchema = z.strictObject({
    templateId: IdSchema,
});
export const WorkflowRunListQuerySchema = ListQuerySchema.extend({
    jobFileId: IdSchema.optional(),
    projectId: IdSchema.optional(),
    phase: WorkflowPhaseSchema.optional(),
});
export const WorkflowRunListSchema = z.strictObject({
    items: z.array(WorkflowRunSchema),
    page: PageInfoSchema,
});
export const MilestoneTaskSchema = z.strictObject({
    id: IdSchema,
    milestoneId: IdSchema,
    key: z.string(),
    label: z.string(),
    description: z.string().nullable(),
    input: MilestoneTaskInputSchema,
    required: z.boolean(),
    evidenceAllowed: z.boolean(),
    sortOrder: z.number().int(),
    completedAt: TimestampSchema.nullable(),
    completedByMembershipId: IdSchema.nullable(),
    valueText: z.string().nullable(),
    valueNumber: z.string().nullable(),
    valueBoolean: z.boolean().nullable(),
    documentId: IdSchema.nullable(),
    signerName: z.string().nullable(),
    version: VersionSchema,
});
export const MilestoneSchema = z.strictObject({
    id: IdSchema,
    organizationId: IdSchema,
    workflowRunId: IdSchema,
    jobFileId: IdSchema,
    projectId: IdSchema.nullable(),
    phase: WorkflowPhaseSchema,
    templateId: IdSchema.nullable(),
    name: z.string(),
    description: z.string().nullable(),
    state: MilestoneStateSchema,
    requiresApproval: z.boolean(),
    sortOrder: z.number().int(),
    dueAt: TimestampSchema.nullable(),
    scheduledAt: TimestampSchema.nullable(),
    startedAt: TimestampSchema.nullable(),
    submittedAt: TimestampSchema.nullable(),
    approvedAt: TimestampSchema.nullable(),
    rejectedAt: TimestampSchema.nullable(),
    completedAt: TimestampSchema.nullable(),
    skippedAt: TimestampSchema.nullable(),
    starred: z.boolean(),
    attention: z.boolean(),
    attentionReason: z.string().nullable(),
    moveRequestReason: z.string().nullable(),
    rejectionReason: z.string().nullable(),
    assignments: AssignmentsSchema,
    tasks: z.array(MilestoneTaskSchema),
    requiredTasksRemaining: z.number().int().nonnegative(),
    version: VersionSchema,
    createdAt: TimestampSchema,
    updatedAt: TimestampSchema,
});
export const MilestoneListQuerySchema = ListQuerySchema.extend({
    jobFileId: IdSchema.optional(),
    workflowRunId: IdSchema.optional(),
    projectId: IdSchema.optional(),
    state: MilestoneStateSchema.optional(),
    assignedMembershipId: IdSchema.optional(),
    dueBefore: TimestampSchema.optional(),
    attention: z.stringbool().optional(),
});
export const MilestoneListSchema = z.strictObject({
    items: z.array(MilestoneSchema),
    page: PageInfoSchema,
});
export const MilestoneVersionCommandSchema = z.strictObject({ version: VersionSchema });
export const ScheduleMilestoneCommandSchema = z.strictObject({
    scheduledAt: TimestampSchema.nullable().default(null),
    dueAt: TimestampSchema.nullable().default(null),
    version: VersionSchema,
});
export const MilestoneReasonCommandSchema = z.strictObject({
    reason: z.string().trim().min(1).max(500),
    version: VersionSchema,
});
export const MilestoneOptionalReasonCommandSchema = z.strictObject({
    reason: z.string().trim().max(500).optional(),
    version: VersionSchema,
});
/** The value for a checklist task depends on its input type; evidence is a private document. */
export const CompleteMilestoneTaskCommandSchema = z.discriminatedUnion('input', [
    z.strictObject({ input: z.literal('checkbox'), version: VersionSchema }),
    z.strictObject({
        input: z.literal('text'),
        value: z.string().trim().min(1).max(2000),
        version: VersionSchema,
    }),
    z.strictObject({ input: z.literal('number'), value: MoneyAmountSchema, version: VersionSchema }),
    z.strictObject({ input: z.literal('file'), documentId: IdSchema, version: VersionSchema }),
    z.strictObject({ input: z.literal('photo'), documentId: IdSchema, version: VersionSchema }),
    z.strictObject({
        input: z.literal('signature'),
        documentId: IdSchema,
        signerName: z.string().trim().min(1).max(200),
        version: VersionSchema,
    }),
]);
